import { useEffect, useState } from 'react';
import axios from 'axios';
import appConstants from '../constants/appConstants';

export function useAcademicYear() {
  const [academicYears, setAcademicYears] = useState([]);
  const [currentYear, setCurrentYear] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch function reused by effect and external triggers
  const fetchYears = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get_academic_years`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.data.status === 'success') {
        const years = response.data.data || [];
        setAcademicYears(years);
        // Prefer numeric `is_current` if provided, otherwise fall back to textual status
        const current =
          years.find((y) => Number(y.is_current) === 1) ||
          years.find((y) => y.status === 'active');
        setCurrentYear(current || years[0] || null);
      }
    } catch (err) {
      setAcademicYears([]);
      setCurrentYear(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchYears();
    // expose a global refresh function for other scripts/components
    try {
      window.__refreshAcademicYears = fetchYears;
    } catch (e) {
      /* ignore */
    }
    // listen for external updates from other parts of the app
    const handler = () => fetchYears();
    window.addEventListener('academicYearsChanged', handler);
    return () => {
      try {
        // cleanup global reference
        if (window.__refreshAcademicYears === fetchYears)
          delete window.__refreshAcademicYears;
      } catch (e) {
        /* ignore */
      }
      window.removeEventListener('academicYearsChanged', handler);
    };
  }, []);

  // Function to set a year as current on the server and refresh local state
  const setCurrentYearOnServer = async (year) => {
    if (!year) return;
    // optimistic local update
    setCurrentYear(year);
    try {
      const token = localStorage.getItem('authToken');
      const instituteId = localStorage.getItem('institute_id') || 1;
      const payload = {
        id: year.id,
        institute_id: parseInt(instituteId, 10),
        is_current: 1,
      };
      await axios.post(
        `${appConstants.API_ROOT_URL}update_academic_year`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      // refresh the list to get updated flags from server
      const resp = await axios.get(
        `${appConstants.API_ROOT_URL}get_academic_years`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (resp.data.status === 'success') {
        const years = resp.data.data || [];
        setAcademicYears(years);
        const current =
          years.find((y) => Number(y.is_current) === 1) ||
          years.find((y) => y.status === 'active');
        setCurrentYear(current || years[0] || null);
        // reload the app so other parts pick up the changed current year
        try {
          window.location.reload();
        } catch (reloadErr) {
          // ignore reload errors in non-browser environments
        }
      }
    } catch (err) {
      // on error, log and leave optimistic change; caller can refetch if needed
      console.error('Error setting current academic year:', err);
    }
  };

  return {
    academicYears,
    currentYear,
    setCurrentYear: setCurrentYearOnServer,
    loading,
  };
}
